import Footer from '../components/Footer.jsx';
import PublicHeader from '../components/PublicHeader.jsx';
import { useTitle } from '../lib/useTitle.js';
import { useMetaTags } from '../lib/useMetaTags.js';

export default function About() {
  useTitle('About');
  useMetaTags({
    description: 'TrainForge is an AI-powered training platform built by a solo developer in Erie, PA: a white-label app for gyms and their trainers, and a self-serve app for lifters who train on their own.',
    ogTitle: 'About TrainForge',
    ogDescription: 'One platform for gym management and individual training, powered by AI that knows your equipment, your members, and your goals.',
    ogType: 'website',
    canonical: 'https://mytrainforge.com/about',
  });
  return (
    <div>
      <PublicHeader />
      <div className="shell" style={{ maxWidth: 720 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div className="wordmark" style={{ fontSize: '2.4rem' }}>TRAIN<span style={{ color: 'var(--text-dim)' }}>FORGE</span></div>
          <h1 className="tagline" style={{ margin: '4px 0 0', fontWeight: 400 }}>AI-POWERED TRAINING. FOR GYMS AND THE PEOPLE WHO LIFT.</h1>
        </div>

        <div style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--text-dim)' }}>
          <p>
            TrainForge started because the gap between gym management tools (expensive, clunky, built for billing) and individual workout trackers (no trainer connection, no real coaching) shouldn't exist. One platform should handle both, and it should be powered by AI that actually knows your equipment, your members, and your goals.
          </p>

          <p>
            Built by a solo developer in Erie, PA, TrainForge is an all-in-one training platform that works two ways: as a white-label app for gyms that want a branded experience for their members and trainers, and as a self-serve app for individuals who train on their own.
          </p>

          <h3 style={{ color: 'var(--text)' }}>For gyms</h3>
          <p>
            White-label app branded with your logo, colors, and subdomain (yourgym.mytrainforge.com). Manage members and trainers with role-based access, generate AI programs from your gym's exact equipment, run compliance dashboards and leaderboards, and communicate through in-app messaging. Each gym's data is fully isolated.
          </p>

          <h3 style={{ color: 'var(--text)' }}>For individuals</h3>
          <p>
            Train on your own without needing a partnered gym. Select your gym profiles (Planet Fitness, home gym, commercial gym, bodyweight only) and your equipment merges automatically. Generate up to 3 draft programs, compare them side by side, and activate the best one. Log food with a barcode scanner or AI photo analysis. Your nutrition targets adapt weekly based on your real weight trend. No generic calculator formulas.
          </p>

          <h3 style={{ color: 'var(--text)' }}>What makes it different</h3>
          <ul>
            <li>The only platform combining gym management + workout tracking + nutrition + AI coaching in one product</li>
            <li>AI programs built from your gym's actual available equipment, not a generic library you have to adapt</li>
            <li>Adaptive nutrition that calculates your real TDEE and auto-adjusts weekly. No other gym platform does this.</li>
            <li>Barcode scanner + AI photo food logging with instant macro breakdown</li>
            <li>AI body-composition change analysis from your progress photos. Per-body-part breakdown (shoulders/chest/back/arms/core/legs) in neutral calibrated language. Only describes direction of change between two photos of you; never claims absolute body-fat state.</li>
            <li>Optional AI starting-parameter personalization at onboarding. One photo and the system suggests a tuned calorie deficit + timeline. Tendency framing only ("tends to leanness" not "you are an ectomorph"); skippable; photos are private to you unless you turn on trainer sharing.</li>
            <li>AI focus-area suggester on Profile. One photo and AI suggests 1–3 body parts to prioritize in your next program. Suggestions feed the existing program generator. Within-body comparison only ("less developed relative to YOUR chest"), never population norms.</li>
            <li>Quick AI workout generator. Pick body parts, where you are, and how much time you have. The AI builds one equipment-aware session sized to the clock and drops you into it. No program required.</li>
            <li>Built for gym owners, trainers, and solo lifters: same app, different roles</li>
            <li>Free during beta, founder pricing locked in for early partners</li>
          </ul>

          <h3 style={{ color: 'var(--text)' }}>Mission</h3>
          <p>
            Make AI-powered coaching accessible to every gym and every lifter, not just those who can afford a $200/month personal trainer.
          </p>

          <p style={{ color: 'var(--text-muted)', marginTop: 24, fontSize: 13 }}>
            TrainForge is built and operated from Erie, PA.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
